import React, { forwardRef, useEffect, useState } from 'react';
import { InputAdornment, TextField, TextFieldProps } from '@mui/material';
import { formatPesosInput, parsePesosInput, roundMoney } from '../../utils/formatters';

export type CurrencyTextFieldProps = Omit<TextFieldProps, 'value' | 'onChange' | 'type'> & {
  value: number;
  onChange: (value: number) => void;
  allowNegative?: boolean;
};

const sanitizeCurrencyInput = (raw: string, allowNegative: boolean): string => {
  const pattern = allowNegative ? /[^0-9.,-]/g : /[^0-9.,]/g;
  let sanitized = String(raw ?? '').replace(pattern, '');
  if (!allowNegative) return sanitized;

  const isNegative = sanitized.startsWith('-');
  sanitized = sanitized.replace(/-/g, '');
  return isNegative ? `-${sanitized}` : sanitized;
};

export const CurrencyTextField = forwardRef<HTMLInputElement, CurrencyTextFieldProps>(
  function CurrencyTextField(
    { value, onChange, allowNegative = false, onFocus, onBlur, InputProps, inputProps, ...rest },
    ref
  ) {
    const [focused, setFocused] = useState(false);
    const [localValue, setLocalValue] = useState(formatPesosInput(value ?? 0));

    useEffect(() => {
      if (!focused) {
        setLocalValue(formatPesosInput(value ?? 0));
      }
    }, [value, focused]);

    return (
      <TextField
        {...rest}
        type="text"
        inputRef={ref}
        value={localValue}
        onFocus={(e) => {
          setFocused(true);
          setLocalValue(formatPesosInput(value ?? 0));
          onFocus?.(e);
        }}
        onChange={(e) => {
          const sanitized = sanitizeCurrencyInput(e.target.value, allowNegative);
          setLocalValue(sanitized);
          onChange(roundMoney(parsePesosInput(sanitized)));
        }}
        onBlur={(e) => {
          setFocused(false);
          const parsed = roundMoney(parsePesosInput(localValue));
          onChange(parsed);
          setLocalValue(formatPesosInput(parsed));
          onBlur?.(e);
        }}
        inputProps={{ inputMode: 'decimal', ...inputProps }}
        InputProps={{
          startAdornment: <InputAdornment position="start">$</InputAdornment>,
          ...InputProps,
        }}
      />
    );
  }
);

export default CurrencyTextField;
